/* eslint-disable react/forbid-prop-types */
import PropTypes from "prop-types";
import { useCallback, useEffect, Fragment, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { ShieldExclamationIcon } from "@heroicons/react/24/outline";

type ModalProps = {
  message?: string;
  confirmModal?: () => void;
  closeModal: () => void;
  show: boolean;
  maxWidth?: number;
  confirmMessage?: string | null;
  confirmCancel?: string | null;
  thirdChoiceConfirmAction?: () => void;
  thirdChoiceConfirmLabel?: string;
  // confirmColor,
  enableIcon?: boolean;
};
function Modal({
  message,
  confirmModal,
  closeModal,
  show,
  maxWidth,
  confirmMessage,
  confirmCancel,
  thirdChoiceConfirmAction,
  thirdChoiceConfirmLabel,
  // confirmColor,
  enableIcon,
}: ModalProps) {
  const { t } = useTranslation();

  const handleEscape = useCallback(
    (e: any) => {
      if (e.keyCode === 27) {
        closeModal();
      }
    },
    [closeModal]
  );

  const cancelButtonRef = useRef(null);

  useEffect(() => {
    document.addEventListener("keyup", handleEscape);

    return () => document.removeEventListener("keyup", handleEscape);
  }, [handleEscape]);

  return ReactDOM.createPortal(
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-100"
        initialFocus={cancelButtonRef}
        onClose={closeModal}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95 w-full md:w-[400px]"
              enterTo="opacity-100 translate-y-0 sm:scale-100 w-full md:w-[400px]"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100 w-full md:w-[400px]"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95 w-full md:w-[400px]"
            >
              <Dialog.Panel
                className={`relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:${maxWidth} sm:w-full max-w-[700px] sm:p-6`}
              >
                <div className="sm:flex sm:items-start">
                  {enableIcon && (
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ShieldExclamationIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                  )}
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-900"
                    >
                      {t("modal.confirmTitle")}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{message}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex w-full justify-center">
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-red-500 px-4 py-2 bg-white text-base font-medium text-gray-700 shadow-sm hover:bg-red-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={confirmModal}
                  >
                    {confirmMessage || t("modal.confirm")}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center items-center rounded-md border border-gray-300 px-4 py-2 bg-white text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={closeModal}
                    ref={cancelButtonRef}
                  >
                    {confirmCancel || t("modal.cancel")}
                  </button>
                  {thirdChoiceConfirmAction && (
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-emerald-600 px-4 py-2 bg-white text-base font-medium text-gray-700 shadow-sm hover:bg-emerald-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => {
                        thirdChoiceConfirmAction();
                        closeModal();
                      }}
                    >
                      {thirdChoiceConfirmLabel}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>,
    document.getElementById("portal-root") as HTMLElement
  );
}

Modal.propTypes = {
  message: PropTypes.any.isRequired,
  confirmModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
};

Modal.defaultProps = {
  maxWidth: "max-w-lg",
  confirmMessage: "modal.confirm",
  confirmCancel: "modal.cancel",
  // confirmColor: "red",
  enableIcon: true,
};

export default Modal;
